import { supabase } from './supabase';
import {
  buildInventoryByCategory,
  monthRangeIso,
  type InventoryCategoryGroup,
} from './inventoryCategoryReport';

interface SalesItemRow {
  quantity: number;
  total_price: number;
  product_name: string;
  stock_item_id: string;
  stock_items: {
    id: string;
    name: string;
    unit: string;
    category_id: string | null;
    category: { id: string; name: string } | null;
  } | null;
}

/** Product sales this month grouped by stock category (line revenue). */
export async function fetchInventorySalesByCategory(
  businessId: string
): Promise<InventoryCategoryGroup[]> {
  if (!supabase) throw new Error('Database connection not available');

  const { start, end } = monthRangeIso();
  const { data, error } = await supabase
    .from('product_sale_items')
    .select(
      `
      quantity,
      total_price,
      product_name,
      stock_item_id,
      product_sales!inner(
        business_id,
        created_at
      ),
      stock_items(
        id,
        name,
        unit,
        category_id,
        category:stock_categories(
          id,
          name
        )
      )
    `
    )
    .eq('product_sales.business_id', businessId)
    .gte('product_sales.created_at', start)
    .lte('product_sales.created_at', end);

  if (error) throw error;

  const entries = ((data ?? []) as SalesItemRow[]).flatMap((row) => {
    const qty = Math.abs(Number(row.quantity) || 0);
    if (qty <= 0) return [];

    const item = row.stock_items;
    const itemId = item?.id ?? row.stock_item_id;

    return [
      {
        itemId,
        name: item?.name ?? row.product_name,
        unit: item?.unit ?? 'unit',
        quantity: qty,
        amount: Number(row.total_price) || 0,
        categoryId: item?.category_id ?? null,
        categoryName: item?.category?.name ?? 'Uncategorized',
      },
    ];
  });

  return buildInventoryByCategory(entries);
}

export type { InventoryCategoryGroup };
